import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Armchair, RectangleHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PageContainer } from '../../../common/components/PageContainer';
import { RugGenerator } from './rug-generator';
import { FurnitureGenerator } from './furniture-generator';

type Kind = 'rug' | 'furniture';
const KINDS: { value: Kind; icon: React.ElementType }[] = [
  { value: 'rug', icon: RectangleHorizontal },
  { value: 'furniture', icon: Armchair },
];

/**
 * The super-admin 3D generator, under Tools: a product's 3D model (.glb) to add
 * it by hand. A rug is made here from its photo and size (a slab, at once); a
 * piece of furniture by fal from its photo (a minute or two). The choice is
 * kept in the address (?kind=furniture), so a link opens on the right one.
 */
export const ModelGeneratorPage: React.FC = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const kind: Kind = params.get('kind') === 'furniture' ? 'furniture' : 'rug';

  return (
    <PageContainer breadcrumbs={[{ label: t('ADMIN_TOOLS.NAV') }, { label: t('ADMIN_TOOLS.MODEL_GENERATOR.TITLE') }]}>
      <div className="mb-6 md:mb-8">
        <h1 className="mb-1 font-heading text-2xl font-medium tracking-tight">{t('ADMIN_TOOLS.MODEL_GENERATOR.TITLE')}</h1>
        <p className="text-sm text-muted-foreground">{t('ADMIN_TOOLS.MODEL_GENERATOR.SUBTITLE')}</p>
      </div>

      <div role="radiogroup" aria-label={t('ADMIN_TOOLS.MODEL_GENERATOR.KIND')} className="mb-6 grid max-w-xl grid-cols-2 gap-3">
        {KINDS.map(({ value, icon: Icon }) => {
          const selected = kind === value;
          const key = value.toUpperCase();
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setParams(value === 'rug' ? {} : { kind: value }, { replace: true })}
              className={cn(
                'flex items-center gap-3 rounded-xl border bg-card p-3 text-start transition-colors hover:bg-accent/40',
                selected && 'border-foreground ring-[0.5px] ring-foreground',
              )}
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Icon className="size-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t(`ADMIN_TOOLS.MODEL_GENERATOR.${key}`)}</span>
                <span className="block text-xs text-muted-foreground">{t(`ADMIN_TOOLS.MODEL_GENERATOR.${key}_HINT`)}</span>
              </span>
            </button>
          );
        })}
      </div>

      {kind === 'rug' ? <RugGenerator /> : <FurnitureGenerator />}
    </PageContainer>
  );
};

export default ModelGeneratorPage;
